import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useGetOneFormSubmission } from "#src/components/hooks/adapters/useFormSubmissions";
import { useGetOneWorkflow } from "#src/components/hooks/adapters/useWorkflows";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { linkToFormSubmissionDetail } from "#src/Routers/links";
import { WorkflowDetailsRoutePath } from "#src/routes/workflows/all/[workflowId]/detail";
import { type WorkflowType } from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React, { ReactNode, useMemo } from "react";
import {
  renderFormSectionName,
  renderFormSubmissionAnswer,
} from "../batteries-included-components/Panels/TablePanels/FormSubmissionsTablePanel.helpers";

const getAssetTriggerAction = (workflowDetails: WorkflowType) => {
  if (!workflowDetails?.asset_trigger) return "";
  const { asset_type, action = "" } = workflowDetails.asset_trigger.metadata;
  const displayAction = ["create", "update", "delete"].includes(action)
    ? `${action}d`
    : action;

  return `${toTitleCaseString(asset_type, { intent: "ui" })} ${toTitleCaseString(displayAction, { intent: "ui" })}`;
};

/**
 * Resolves workflow trigger key–value data for display. Fetches the canonical
 * workflow by id (when available), loads the triggering form submission and
 * triggering workflow name when needed, and merges with any client-enriched
 * fields already present on the passed workflow (e.g. from workflow detail).
 */
const useWorkflowTriggersKeyValueData = (workflowDetails?: WorkflowType) => {
  const workflowId = workflowDetails?.id ?? "";

  const workflowQuery = useGetOneWorkflow(
    { id: workflowId },
    { enabled: Boolean(workflowId) }
  );

  const workflowFromApi = workflowQuery.data?.data;

  const workflowForTriggerResolution = workflowFromApi ?? workflowDetails;

  const formSubmissionId =
    workflowForTriggerResolution?.form_trigger?.form_submission_id ?? "";

  const formSubmissionQuery = useGetOneFormSubmission(
    { id: formSubmissionId },
    { enabled: Boolean(formSubmissionId) }
  );

  const triggeringWorkflowId =
    workflowForTriggerResolution?.workflow_trigger?.triggering_workflow_id ??
    "";

  const triggeringWorkflowQuery = useGetOneWorkflow(
    { id: triggeringWorkflowId },
    { enabled: Boolean(triggeringWorkflowId) }
  );

  const {
    measurementUnits,
    getPrecisionByType,
    isLoading: isMeasurementTypesLoading,
  } = useMeasurementTypes();

  const enrichedWorkflow = useMemo((): WorkflowType | undefined => {
    const base = workflowFromApi ?? workflowDetails;
    if (!base) {
      return undefined;
    }

    const passedFormTrigger = workflowDetails?.form_trigger as any;
    const formSubmissionFromPassed =
      workflowDetails?.id === base.id
        ? passedFormTrigger?.form_submission
        : undefined;

    const formSubmissionPayload =
      formSubmissionQuery.data ?? formSubmissionFromPassed;

    const triggeringWorkflowName =
      triggeringWorkflowQuery.data?.data?.name ??
      (workflowDetails?.id === base.id
        ? workflowDetails?.workflow_trigger?.triggering_workflow_name
        : undefined);

    return {
      ...base,
      form_trigger: base.form_trigger
        ? {
            ...base.form_trigger,
            ...(formSubmissionPayload
              ? { form_submission: formSubmissionPayload }
              : {}),
          }
        : base.form_trigger,
      workflow_trigger: base.workflow_trigger
        ? {
            ...base.workflow_trigger,
            ...(triggeringWorkflowName
              ? { triggering_workflow_name: triggeringWorkflowName }
              : {}),
          }
        : base.workflow_trigger,
    };
  }, [
    workflowFromApi,
    workflowDetails,
    formSubmissionQuery.data,
    triggeringWorkflowQuery.data,
  ]);

  const populatedData = useMemo(() => {
    const wf = enrichedWorkflow;
    const formTriggerRef = wf?.form_trigger as any;

    const formTrigger: Array<{ title: string; value: ReactNode }> = wf
      ?.form_trigger?.form_submission_id
      ? [
          {
            title: "Trigger",
            value: "Form Submission",
          },
          {
            title: "Triggering Form Submission",
            value: formTriggerRef?.form_submission ? (
              <RoutingLink
                to={linkToFormSubmissionDetail(
                  wf.form_trigger.form_submission_id
                )}
              >
                {`${formTriggerRef.form_submission?.data?.form_schema?.name} - ${wf.form_trigger.form_submission_id.slice(0, 7)}`}
              </RoutingLink>
            ) : (
              "-"
            ),
          },
        ]
      : [];

    const { section_id, section_index } =
      wf?.form_trigger?.metadata ?? ({} as any);

    const formTriggerDetails: Array<{ title: string; value: ReactNode }> = (
      wf?.form_trigger?.metadata as any
    )?.answers
      ? Object.entries((wf?.form_trigger?.metadata as any)?.answers)?.map(
          ([question_id, answer]: any) => ({
            title: formTriggerRef?.form_submission?.data?.form_schema
              ? `${renderFormSectionName(section_id, section_index, formTriggerRef.form_submission?.data.form_schema)}` +
                " : " +
                (formTriggerRef.form_submission?.data.form_schema?.config
                  ?.questions[question_id]?.prompt || "-")
              : "-",
            value: formTriggerRef?.form_submission?.data?.form_schema
              ? renderFormSubmissionAnswer(answer, question_id, {
                  schema: formTriggerRef.form_submission?.data.form_schema,
                  measurementUnits,
                  getPrecisionByType,
                } as any)
              : "-",
          })
        )
      : [];

    const assetTrigger: Array<{ title: string; value: ReactNode }> = wf
      ?.asset_trigger?.metadata?.asset_type
      ? [
          {
            title: "Trigger",
            value: getAssetTriggerAction(wf),
          },
        ]
      : [];
    const workflowTrigger: Array<{ title: string; value: ReactNode }> = wf
      ?.workflow_trigger?.triggering_workflow_id
      ? [
          {
            title: "Trigger",
            value: "Workflow Completion",
          },
          {
            title: "Triggering Workflow",
            value: wf?.workflow_trigger?.triggering_workflow_name ? (
              <RoutingLink
                to={WorkflowDetailsRoutePath.toLinkParts({
                  pathParams: {
                    workflowId: wf.workflow_trigger.triggering_workflow_id,
                  },
                })}
              >
                {`${wf.workflow_trigger.triggering_workflow_name}`}
              </RoutingLink>
            ) : (
              "-"
            ),
          },
        ]
      : [];

    return {
      trigger: formTrigger.concat(assetTrigger).concat(workflowTrigger),
      formTriggerDetails,
    };
  }, [enrichedWorkflow, measurementUnits, getPrecisionByType]);

  const areThereAnyTriggers =
    populatedData.trigger.length + populatedData.formTriggerDetails.length > 0;

  const isLoading =
    isMeasurementTypesLoading ||
    (Boolean(workflowId) && workflowQuery.isLoading) ||
    (Boolean(formSubmissionId) && formSubmissionQuery.isLoading) ||
    (Boolean(triggeringWorkflowId) && triggeringWorkflowQuery.isLoading);

  return { ...populatedData, isLoading, areThereAnyTriggers };
};

export default useWorkflowTriggersKeyValueData;
